/**
 * Bulk variant selection for Dawn 15.4.
 *
 * Renders one or more toggle pills under the variant picker — "Add all 4
 * available colors". Selecting one behaves like picking a variant: the price
 * above updates to the batch total, and Dawn's own Add to cart button then
 * adds every variant in the range at the chosen quantity.
 *
 * Selecting a pill also hides the dynamic checkout button, which can only ever
 * buy the single selected variant.
 */

// Matched as a case-insensitive substring against the option's NAME, so
// "Barrel Color", "Ink Color", "Colorway", "Barrel Design" all qualify.
// Audited against the full catalogue: no false positives, and every
// multi-option product resolves to at least one axis.
const AXIS_PATTERN = /colou?r|density|design/i;

// Legacy "Complete Bundle (12)" variants — the old way of offering this same
// feature. Excluded everywhere: counts, prices and the cart payload. Matched
// against option VALUES. Audited: 49 distinct values across 51 products, all
// spelled "Complete Bundle". Delete this once those variants are retired.
const BUNDLE_PATTERN = /complete\s*bundle/i;

class BulkAddVariants extends HTMLElement {
  connectedCallback() {
    this.allBtn = this.querySelector('[data-all]');
    this.prices = JSON.parse(this.querySelector('[data-prices]').textContent);
    this.cart =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.form = document.getElementById(this.dataset.formId);
    this.priceEl = document.getElementById(`price-${this.dataset.sectionId}`);
    this.submitBtn = this.form?.querySelector('[name="add"]');
    this.dynamicCheckout = this.form?.querySelector('[data-shopify="payment-button"]');

    this.active = null; // the selected pill, or null when off

    this.allBtn.addEventListener('click', () => this.toggle(null, this.allBtn));

    // Dawn binds its handler on the form itself (product-form.js:10), so a
    // capture-phase listener on document runs first and can take the submit
    // over entirely when a bulk option is selected.
    this.onSubmit = (event) => {
      if (event.target !== this.form || !this.active) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.submit();
    };
    document.addEventListener('submit', this.onSubmit, true);

    // Picking a real value on the axis we're batching means the shopper wants
    // that one variant after all, so the pill steps aside. `cleared` is dropped
    // rather than restored — their click is the newer intent.
    this.onPickerChange = (event) => {
      if (!this.active) return;
      const group = this.optionGroup(this.activeAxis ?? 0);
      if (!group?.contains(event.target)) return;
      this.cleared = null;
      this.active = null;
      this.activeAxis = null;
      this.render();
    };
    document.addEventListener('change', this.onPickerChange, true);

    // Dawn updates price/media/selectors in place rather than replacing the
    // form, so we track the selection through its pub/sub instead of the DOM.
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data.sectionId !== this.dataset.sectionId) return;
      this.variant = event.data.variant;
      this.render();
    });

    this.load();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    document.removeEventListener('submit', this.onSubmit, true);
    document.removeEventListener('change', this.onPickerChange, true);
    this.active = null;
    this.paint(); // hand the price, checkout button and picker back to Dawn
  }

  async load() {
    this.product = await fetch(`${this.dataset.productUrl}.js`).then((r) => r.json());
    this.optionNames = this.product.options.map((o) => (typeof o === 'string' ? o : o.name));

    if (!this.variant) {
      const id = Number(this.variantInput()?.value);
      this.variant = this.product.variants.find((v) => v.id === id);
    }

    this.buildAxes();
    this.render();
  }

  // getElementById rather than querySelector('#' + id): the latter throws a
  // SyntaxError if product_form_id is ever empty or not a valid selector.
  variantInput() {
    const id = this.dataset.formId;
    return (
      (id && document.getElementById(id)?.querySelector('[name="id"]')) ||
      document.querySelector('product-form form[action*="/cart/add"] [name="id"]')
    );
  }

  // Dawn's stepper sits outside the form and is wired to it with form="…",
  // so it's reachable as a form-associated element rather than a descendant.
  quantityInput() {
    return document.getElementById(this.dataset.formId)?.elements?.quantity ?? null;
  }

  quantity() {
    const n = Math.floor(Number(this.quantityInput()?.value));
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  // Mirrors Dawn's handleErrorMessage so it inherits the theme's styling.
  handleError(message = '') {
    this.errorWrapper ??= this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorWrapper) return;
    this.errorWrapper.toggleAttribute('hidden', !message);
    if (message) {
      this.errorWrapper.querySelector('.product-form__error-message').textContent = message;
    }
  }

  // One pill per batchable option. Option order is inconsistent across the
  // catalogue, so axes are found by name rather than by position.
  buildAxes() {
    if (this.axes) return;
    const tpl = this.querySelector('[data-button-template]');
    const host = this.querySelector('[data-axes]');

    this.axes = this.optionNames
      .map((name, index) => ({ name, index }))
      .filter(({ name }) => AXIS_PATTERN.test(name))
      .map(({ name, index }) => {
        const btn = tpl.content.firstElementChild.cloneNode(true);
        btn.hidden = true;
        btn.addEventListener('click', () => this.toggle(index, btn));
        host.appendChild(btn);
        return { btn, index, name };
      });
  }

  // Every variant on an axis, sold out included.
  // axis === null -> the whole product.
  // axis === n    -> hold all other options at the current selection, vary n.
  matching(axis) {
    const sellable = this.product.variants.filter(
      (v) => !v.options.some((opt) => BUNDLE_PATTERN.test(opt))
    );
    if (axis === null) return sellable;
    if (!this.variant) return [];
    return sellable.filter((v) =>
      v.options.every((opt, i) => i === axis || opt === this.variant.options[i])
    );
  }

  // The subset that can actually be added to the cart.
  select(axis) {
    return this.matching(axis).filter((v) => v.available);
  }

  plural(name) {
    const n = name.trim().toLowerCase().replace(/\s+/g, ' ');
    return n.endsWith('s') ? n : `${n}s`;
  }

  // "4 colors" when the whole range is in stock, "4 available colors" when it
  // isn't — so the number always reconciles with what the shopper can see.
  countLabel(axis, name) {
    const inStock = this.select(axis).length;
    const qualifier = inStock < this.matching(axis).length ? 'available ' : '';
    return `${inStock} ${qualifier}${this.plural(name)}`;
  }

  // Prices are read from the Liquid-rendered map, which is in the shopper's
  // presentment currency with per-market rounding already applied. The
  // /products/x.js payload is used only for availability and options — it can
  // report base-currency prices to shoppers browsing in another market.
  priceOf(variant) {
    return this.prices[variant.id]?.price ?? variant.price;
  }

  // Falls back to the selling price, so variants that aren't discounted still
  // contribute their full value to the struck-through "was" total.
  compareOf(variant) {
    const price = this.priceOf(variant);
    const compare = this.prices[variant.id]?.compare ?? 0;
    return compare > price ? compare : price;
  }

  money(minorUnits) {
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency,
      currencyDisplay: 'narrowSymbol',
    }).format(minorUnits / 100);
    // Dawn appends the ISO code when currency_code_enabled ("$22.90 USD").
    return this.dataset.currencyCodeEnabled === 'true'
      ? `${formatted} ${this.dataset.currency}`
      : formatted;
  }

  // What one set costs. Deliberately not multiplied by the quantity stepper:
  // Dawn shows a unit price and lets the cart do the multiplying, and the
  // price line here follows that convention.
  //
  // A set counts as on sale if anything in it is discounted; the "was" figure
  // is the undiscounted worth of the whole set.
  totals(set) {
    const price = set.reduce((sum, v) => sum + this.priceOf(v), 0);
    const compare = set.reduce((sum, v) => sum + this.compareOf(v), 0);
    return { price, compare, onSale: compare > price };
  }

  priceNote(set) {
    return this.money(this.totals(set).price);
  }

  // Clone Dawn's own price block and swap the numbers, rather than rebuilding
  // it. Both .price__regular and .price__sale are always present — .price--on-sale
  // decides which shows and reveals the Sale badge — so cloning inherits the
  // theme's translations, badge colour scheme and any customisation for free.
  priceMarkup(set) {
    const { price, compare, onSale } = this.totals(set);
    const source = this.priceEl?.querySelector('.price');
    if (!source) {
      return `<div class="price price--large"><div class="price__container"><div class="price__regular">
        <span class="price-item price-item--regular">${this.money(price)}</span>
      </div></div></div>`;
    }

    const node = source.cloneNode(true);
    node.classList.toggle('price--on-sale', onSale);
    node.classList.remove('price--sold-out'); // a batch only ever contains in-stock variants

    const put = (selector, text) => {
      const el = node.querySelector(selector);
      if (el) el.textContent = text;
    };
    put('.price__regular .price-item--regular', this.money(price));
    put('.price__sale s', this.money(compare));
    put('.price__sale .price-item--sale', this.money(price));

    return node.outerHTML;
  }

  toggle(axis, btn) {
    const turningOff = this.active === btn;
    this.active = turningOff ? null : btn;
    this.activeAxis = turningOff ? null : axis;
    this.handleError();
    this.showHeroImage(!turningOff);
    this.render();
  }

  // The media entry behind product.featured_image — the group shot used on
  // collection pages. Falls back to the first media, which differs only when
  // the product leads with a video.
  heroMediaId() {
    const media = this.product?.media ?? [];
    const base = (url) => (url || '').split('?')[0];
    const featured = base(this.product?.featured_image);
    // `||` not `??`: base() yields '' for a missing featured_image, and ''
    // would satisfy `??`, skipping the fallback.
    const match = featured ? media.find((m) => base(m.src) === featured) : null;
    return (match || media[0])?.id ?? null;
  }

  // Dawn swaps the hero to the selected variant's own image. A batch isn't one
  // variant, so show the group shot instead — and put the variant's image back
  // when the pill is switched off.
  //
  // Driven only from toggle(), never from render(): setActiveMedia scrolls the
  // page when the image is out of view, so calling it on every re-render would
  // yank the page around. Deselecting via a swatch needs no call here, because
  // Dawn sets the media itself on variantChange.
  showHeroImage(on) {
    const gallery =
      document.getElementById(`MediaGallery-${this.dataset.sectionId}`) ||
      document.querySelector('media-gallery');
    if (typeof gallery?.setActiveMedia !== 'function' || !this.product) return;

    const variantMedia = this.product.variants.find((v) => v.id === this.variant?.id)
      ?.featured_media?.id;
    const mediaId = on ? this.heroMediaId() : variantMedia;
    if (!mediaId) return;

    gallery.setActiveMedia(`${this.dataset.sectionId}-${mediaId}`, true);
  }

  // Dawn renders one <fieldset> per option, in option order.
  optionGroup(index) {
    const picker =
      document.getElementById(`variant-selects-${this.dataset.sectionId}`) ||
      document.querySelector(`variant-selects[data-section="${this.dataset.sectionId}"]`);
    return picker?.querySelectorAll('fieldset')[index] ?? null;
  }

  // While a pill is active the single variant it replaces shouldn't look
  // selected. Only the axis being batched is cleared — options held fixed
  // ("in Medium") stay lit, because the shopper still needs to see them.
  // Unchecking is display-only: it fires no change event, and Dawn reads the
  // variant from the form's hidden id input, not from these radios.
  syncVariantPicker() {
    if (this.cleared) {
      if (this.cleared.isConnected) this.cleared.checked = true;
      this.cleared = null;
    }
    if (!this.active) return;

    const group = this.optionGroup(this.activeAxis ?? 0);
    const checked = group?.querySelector('input[type="radio"]:checked');
    if (!checked) return;
    checked.checked = false;
    this.cleared = checked;
  }

  // Our own price line, inserted beside Dawn's. Swapping visibility beats
  // rewriting Dawn's markup: Dawn keeps re-rendering its own element on
  // variant change, and we never have to restore a stale copy of it.
  totalEl() {
    if (!this.bulkPrice && this.priceEl) {
      this.bulkPrice = document.createElement('div');
      // Deliberately NOT `.price` — Dawn's #price-{section} is a bare div with
      // the .price class on its child, and .price zeroes margin-bottom. Putting
      // it on the outer element leaves .product__tax's margin-top: -1.4rem with
      // nothing to cancel, so "Shipping calculated at checkout" rides up 14px.
      this.bulkPrice.className = 'bulk-add-variants__total';
      this.bulkPrice.setAttribute('role', 'status');
      this.bulkPrice.style.display = 'none';
      this.priceEl.insertAdjacentElement('afterend', this.bulkPrice);
    }
    return this.bulkPrice;
  }

  render() {
    if (!this.product) return;

    const all = this.select(null);
    const single = this.optionNames.length === 1;

    // "Add everything" only makes sense when there's one option to vary.
    this.allBtn.hidden = !single || all.length < 2;
    if (!this.allBtn.hidden) {
      const noun = AXIS_PATTERN.test(this.optionNames[0]) ? this.optionNames[0] : 'option';
      this.allBtn.querySelector('[data-label]').textContent =
        `Add all ${this.countLabel(null, noun)}`;
    }

    this.axes.forEach(({ btn, index, name }) => {
      const set = this.select(index);
      // Hide if there's nothing to batch, or it would duplicate a visible
      // "add all" pill.
      btn.hidden = set.length < 2 || (!this.allBtn.hidden && set.length === all.length);
      if (btn.hidden) return;

      const held = this.variant.options.filter((_, i) => i !== index).join(' / ');
      btn.querySelector('[data-label]').textContent =
        `Add all ${this.countLabel(index, name)} in ${held}`;
    });

    // A pill that just became irrelevant can't stay selected.
    if (this.active?.hidden) {
      this.active = null;
      this.activeAxis = null;
    }

    this.paint();
  }

  paint() {
    const on = Boolean(this.active);

    for (const btn of this.querySelectorAll('button')) {
      btn.setAttribute('aria-pressed', String(btn === this.active));
    }

    this.syncVariantPicker();

    // style.display rather than the hidden attribute: Dawn's author-level
    // rules on .price and .button outrank the user-agent [hidden] rule.
    if (this.priceEl) this.priceEl.style.display = on ? 'none' : '';

    const total = this.totalEl();
    if (total) {
      total.style.display = on ? '' : 'none';
      if (on) total.innerHTML = this.priceMarkup(this.select(this.activeAxis));
    }

    // Shop Pay / dynamic checkout can only ever buy the one selected variant,
    // so it must not be reachable while a bulk option is active.
    if (this.dynamicCheckout) this.dynamicCheckout.style.display = on ? 'none' : '';
  }

  // Dawn's cart-notification is built for adding ONE line: renderContents reads
  // `parsedState.key` and builds a selector from it (cart-notification.js:38).
  // A multi-item /cart/add.js response has no top-level key, so the selector
  // becomes [id="cart-notification-product-undefined"], querySelector returns
  // null, and getSectionInnerHTML throws. The section renders a block for every
  // cart item, so any real key resolves — prefer the variant the shopper was
  // actually looking at.
  withLineKey(json) {
    const items = json.items ?? [];
    if (!items.length || json.key) return json;
    const preferred = items.find((i) => i.id === this.variant?.id) ?? items[0];
    return { ...json, key: preferred?.key };
  }

  // The notification's product block describes a single line item, so after a
  // batch it reads "Ink Color: Baby Pink" when fifteen colours went in.
  // Collapse the batched option to a count instead. Dawn re-renders this block
  // on every add, so the patch clears itself on the next single add — there is
  // deliberately nothing to restore.
  summariseNotification(count) {
    if (count < 2) return;
    const name = this.optionNames?.[this.activeAxis ?? 0];
    const host = document.getElementById('cart-notification-product');
    if (!name || !host) return;

    for (const row of host.querySelectorAll('.product-option')) {
      const label = row.querySelector('dt')?.textContent.replace(':', '').trim().toLowerCase();
      if (label !== name.trim().toLowerCase()) continue;
      const value = row.querySelector('dd');
      if (value) value.textContent = `All ${count}`;
    }
  }

  async submit() {
    const generic = window.cartStrings?.error || 'Could not add these items to your cart.';
    const btn = this.submitBtn;
    this.handleError();
    btn?.setAttribute('aria-disabled', 'true');
    btn?.classList.add('loading');
    btn?.querySelector('.loading__spinner')?.classList.remove('hidden');

    try {
      // /cart/add.js is all-or-nothing: one sold-out line fails the whole
      // request, so re-check availability rather than trusting page render.
      await this.load();
      const quantity = this.quantity();
      const items = this.select(this.activeAxis).map((v) => ({ id: v.id, quantity }));
      if (!items.length) throw new Error(generic);

      const body = { items };
      if (this.cart) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        this.cart.setActiveElement(document.activeElement);
      }

      const json = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .catch(() => null); // network or non-JSON response

      if (!json) throw new Error(generic);
      // Shopify returns `status` only on failure; `description` is the
      // shopper-facing reason ("All 3 of Axolotl Red are in your cart.").
      if (json.status) throw new Error(json.description || json.message || generic);

      if (this.cart) {
        this.cart.renderContents(this.withLineKey(json));
        this.summariseNotification(items.length);
      } else {
        window.location = routes.cart_url;
      }
    } catch (error) {
      console.error('[bulk-add-variants]', error);
      this.handleError(error.message || generic);
    } finally {
      btn?.removeAttribute('aria-disabled');
      btn?.classList.remove('loading');
      btn?.querySelector('.loading__spinner')?.classList.add('hidden');
      this.render();
    }
  }
}

customElements.define('bulk-add-variants', BulkAddVariants);
